import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormControlLabel, Radio, RadioGroup, TextField, Typography, Chip,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import api from '../../services/api';
import { toastError, toastSuccess } from '../../services/alerts';

const FMT_MOEDA = (v) => `R$ ${Number(v || 0).toFixed(2)}`;
const FMT_DATA = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('pt-BR') : '-');

/**
 * Escolha da baixa a estornar.
 *
 * O backend já aceitava estorno de uma baixa específica (`idMovimentacao`), mas a
 * interface só oferecia "estornar" em título PAGO e sempre mandava o pedido sem
 * id — ou seja, em título PARCIAL com duas baixas não havia como corrigir a
 * errada. Aqui o extrato do título é exibido, o usuário escolhe entre as baixas
 * ATIVAS (valor, data e conta visíveis), informa o motivo e o id escolhido vai no
 * pedido. Baixa já estornada aparece marcada e não é selecionável.
 *
 * @param recurso 'contas-receber' ou 'contas-pagar'
 */
export default function EstornoMovimentacaoDialog({ open, onClose, recurso, idTitulo, descricaoTitulo, onEstornado }) {
    const [movimentacoes, setMovimentacoes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState(null);
    const [selecionada, setSelecionada] = useState('');
    const [motivo, setMotivo] = useState('');
    const [enviando, setEnviando] = useState(false);

    const ehBaixa = recurso === 'contas-receber'
        ? (m) => m.tipo === 'RECEBIMENTO'
        : (m) => m.tipo === 'PAGAMENTO';

    const carregar = useCallback(async () => {
        if (!idTitulo) return;
        setLoading(true);
        setErro(null);
        try {
            const { data } = await api.get(`/${recurso}/${idTitulo}/movimentacoes`);
            setMovimentacoes(Array.isArray(data) ? data : []);
        } catch (e) {
            // Falha de carregamento não pode parecer "título sem movimentação".
            setErro('Não foi possível carregar o extrato do título.');
            setMovimentacoes([]);
        } finally {
            setLoading(false);
        }
    }, [recurso, idTitulo]);

    useEffect(() => {
        if (open) {
            setSelecionada('');
            setMotivo('');
            carregar();
        }
    }, [open, carregar]);

    const idsEstornados = new Set(
        movimentacoes.filter(m => m.idMovimentacaoEstornada != null).map(m => m.idMovimentacaoEstornada)
    );
    const baixas = movimentacoes.filter(ehBaixa);
    const ativas = baixas.filter(m => !idsEstornados.has(m.id));

    const confirmar = async () => {
        if (!selecionada) {
            toastError('Escolha a baixa que será estornada.');
            return;
        }
        if (!motivo.trim()) {
            toastError('Informe o motivo do estorno.');
            return;
        }
        setEnviando(true);
        try {
            await api.patch(`/${recurso}/${idTitulo}/estornar-pagamento`, {
                idMovimentacao: Number(selecionada),
                motivo: motivo.trim(),
            });
            toastSuccess('Estorno registrado com sucesso!');
            onClose?.();
            onEstornado?.();
        } catch (e) {
            console.error(e);
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <UndoIcon fontSize="small" /> Estornar baixa
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {descricaoTitulo || `Título #${idTitulo}`}
                </Typography>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
                )}

                {!loading && erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

                {!loading && !erro && ativas.length === 0 && (
                    <Alert severity="info">Este título não tem baixa ativa para estornar.</Alert>
                )}

                {!loading && !erro && ativas.length > 0 && (
                    <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                            aria-label="Baixa a estornar"
                            value={selecionada}
                            onChange={(e) => setSelecionada(e.target.value)}
                        >
                            {ativas.map((m) => (
                                <FormControlLabel
                                    key={m.id}
                                    value={String(m.id)}
                                    control={<Radio />}
                                    sx={{ alignItems: 'flex-start', mb: 1 }}
                                    label={(
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {FMT_MOEDA(m.valor)} em {FMT_DATA(m.dataMovimentacao)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Conta: {m.contaBancaria || 'não informada'}
                                                {m.formaPagamento ? ` · ${m.formaPagamento}` : ''}
                                                {m.autor ? ` · por ${m.autor}` : ''}
                                            </Typography>
                                        </Box>
                                    )}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                )}

                {!loading && !erro && baixas.length > ativas.length && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">Já estornadas:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                            {baixas.filter(m => idsEstornados.has(m.id)).map(m => (
                                <Chip key={m.id} size="small" variant="outlined"
                                    label={`${FMT_MOEDA(m.valor)} · ${FMT_DATA(m.dataMovimentacao)} · estornada`} />
                            ))}
                        </Box>
                    </Box>
                )}

                <TextField
                    label="Motivo do estorno"
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    sx={{ mt: 3 }}
                    helperText="Obrigatório: é o que deixa a correção auditável no extrato."
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={enviando}>Voltar</Button>
                <Button
                    onClick={confirmar}
                    variant="contained"
                    color="warning"
                    disabled={enviando || loading || !!erro || ativas.length === 0}
                    startIcon={enviando ? <CircularProgress size={16} /> : <UndoIcon />}
                >
                    Estornar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
